import type { AuthProvider } from '@nicheaim/nicheaim-timetracking-webcomponent/tracker';
import { getNicheAimEmbedToken, type NicheAimEmbedToken } from './actions';

const TOKEN_RETRY_COOLDOWN_MS = 60_000;

export class NicheAimTokenRateLimitError extends Error {
  readonly status = 429;

  constructor(message: string) {
    super(message);
    this.name = 'NicheAimTokenRateLimitError';
  }
}

const getHttpStatus = (error: unknown): unknown => {
  if (!error || typeof error !== 'object') return undefined;

  const candidate = error as {
    status?: unknown;
    response?: { status?: unknown } | null;
  };
  return candidate.response?.status ?? candidate.status;
};

export function createNicheAimTimeTrackingAuthProvider(
  rateLimitMessage: () => string,
  requestToken: () => Promise<NicheAimEmbedToken> = getNicheAimEmbedToken
): AuthProvider {
  let retryAfter = 0;
  let cached: { token: NicheAimEmbedToken; expiresAt: number } | undefined;
  let pendingRequest: { forceRefresh: boolean; promise: Promise<NicheAimEmbedToken> } | undefined;

  const requestTokenWithCooldown = async (): Promise<NicheAimEmbedToken> => {
    try {
      return await requestToken();
    } catch (error) {
      if (getHttpStatus(error) !== 429) throw error;

      retryAfter = Date.now() + TOKEN_RETRY_COOLDOWN_MS;
      throw new NicheAimTokenRateLimitError(rateLimitMessage());
    }
  };

  return {
    async getAccessToken(options: { forceRefresh?: boolean } = {}) {
      if (Date.now() < retryAfter) {
        throw new NicheAimTokenRateLimitError(rateLimitMessage());
      }

      const forceRefresh = options.forceRefresh === true;
      if (forceRefresh) cached = undefined;
      while (pendingRequest) {
        if (!forceRefresh || pendingRequest.forceRefresh) {
          return pendingRequest.promise;
        }

        try {
          await pendingRequest.promise;
        } catch (_error) {
          // A forced refresh still gets a fresh request after a non-429 failure.
        }

        if (Date.now() < retryAfter) {
          throw new NicheAimTokenRateLimitError(rateLimitMessage());
        }
      }

      if (!forceRefresh && cached && Date.now() + 30_000 < cached.expiresAt) return cached.token;
      const promise = requestTokenWithCooldown();
      pendingRequest = { forceRefresh, promise };
      try {
        const token = await promise;
        cached = { token, expiresAt: Date.now() + token.expires_in * 1000 };
        return token;
      } finally {
        if (pendingRequest?.promise === promise) pendingRequest = undefined;
      }
    },
  };
}
